import * as React from 'react'

import {
  BellIcon,
  HouseIcon,
  MapPinIcon,
  PackageIcon,
  RadioIcon,
  TruckIcon,
  WrenchIcon,
} from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { createPortal } from 'react-dom'

import mobileRoutes from '~/client/src/mobile/constants/mobileRoutes'
import ChatIconWithBadge from '~/client/src/shared/components/ChatIconWithBadge/ChatIconWithBadge'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import { BrandStruxhubGrayIcon } from '~/client/src/shared/components/icons/BrandStruxhubGrayIcon'
import { CaretDownIcon } from '~/client/src/shared/components/icons/CaretDownIcon'
import { CaretUpIcon } from '~/client/src/shared/components/icons/CaretUpIcon'
import { CheckIcon } from '~/client/src/shared/components/icons/CheckIcon'
import { MenuIcon } from '~/client/src/shared/components/icons/MenuIcon'
import { PhotoIcon } from '~/client/src/shared/components/icons/PhotoIcon'
import { QrCodeIcon } from '~/client/src/shared/components/icons/QrCodeIcon'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { GenericWorkflowIcon } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ChatService from '~/client/src/shared/services/ChatService/Chat.service'
import NotificationsStore from '~/client/src/shared/stores/domain/Notifications.store'

import MobileInitialState from '../../stores/MobileInitialState'
import MobileCommonStore from '../../stores/ui/MobileCommon.store'

interface IProps {
  onCreateClicked?(): void

  state?: MobileInitialState
  common?: MobileCommonStore
  chatService?: ChatService
  notificationsStore?: NotificationsStore
}

interface IButton {
  title: string
  icon: any
  onClick: () => void
}

const NAV_BUTTON_BASE =
  'tw:flex tw:flex-col tw:flex-none tw:items-center tw:justify-center ' +
  'tw:h-full tw:w-[75px] tw:px-1.5 tw:py-1 tw:cursor-pointer tw:select-none ' +
  'tw:[&_svg]:size-6 tw:[&_svg]:shrink-0 tw:[&_svg]:fill-current'

// The nav and menu paint their filled glyphs through `[&_svg]:fill-*`; a stroked
// Lucide glyph has to opt out or it renders as a solid shape.
const LUCIDE_IN_FILLED_SLOT = 'tw:fill-none!'

const NAV_BUTTON_DEFAULT = 'tw:text-md3-on-surface-variant'
const NAV_BUTTON_SELECTED = 'tw:text-md3-orange'

const MENU_POPUP =
  'tw:fixed tw:inset-x-0 tw:bottom-[calc(60px_+_env(safe-area-inset-bottom)_-_1px)] tw:z-[101] tw:w-full ' +
  'tw:max-h-[400px] tw:overscroll-none tw:overflow-auto ' +
  'tw:bg-md3-surface-container ' +
  'tw:border-0 tw:border-t tw:border-solid tw:border-md3-outline-variant ' +
  'tw:rounded-t-[4px]'

const MENU_ROW_BASE =
  'tw:flex tw:items-center tw:h-[60px] tw:px-[15px] tw:cursor-pointer ' +
  'tw:[&_svg]:size-6'

@inject('state', 'common', 'chatService', 'notificationsStore')
@observer
export default class BottomNavigationNavBar extends React.Component<IProps> {
  @observable private isMenuPopupShown: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidMount() {
    this.props.common.registerBottomNavBar()
  }

  public componentWillUnmount() {
    this.props.common.unregisterBottomNavBar()
  }

  public render() {
    const { common } = this.props

    if (!common.shouldShowBottomNavBar) {
      return null
    }

    // Transparent spacer when native shell owns the bar (override flag, not WebView presence).
    if (common.isBottomNavBarOverridden) {
      return (
        <div
          aria-hidden="true"
          className="tw:w-full tw:pointer-events-none tw:h-[calc(60px_+_env(safe-area-inset-bottom))]"
        />
      )
    }

    const {
      onCreateClicked,
      state: { isLogisticsDisabled },
    } = this.props

    return (
      <div>
        {this.isMenuPopupShown && this.renderMenu()}
        <div className="tw:pb-safe tw:bg-md3-surface-container tw:overflow-x-auto tw:overflow-y-hidden">
          <div className="tw:flex tw:items-center tw:justify-between tw:h-[60px]">
            {this.renderSideBarMenuButton()}
            {!isLogisticsDisabled &&
              this.renderNavigateButton(mobileRoutes.HOME)}
            {this.renderNavigateMenuButton()}
            {this.renderNavigateButton(mobileRoutes.CHAT)}
            {!!onCreateClicked && (
              <div
                data-testid="bottom-nav-create"
                onClick={onCreateClicked}
                className="tw:flex tw:items-center tw:justify-center tw:ml-2.5 tw:min-w-[55px] tw:max-w-[55px] tw:cursor-pointer"
              >
                <div className="tw:flex tw:items-center tw:justify-center tw:size-[40px] tw:rounded-full tw:bg-md3-primary tw:text-md3-on-primary">
                  <AddIcon />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  private renderSideBarMenuButton(): JSX.Element {
    return (
      <div
        onClick={this.props.common.toggleSidebar}
        className={`${NAV_BUTTON_BASE} ${NAV_BUTTON_DEFAULT}`}
        data-testid="mobile-sidebar-toggle"
      >
        <MenuIcon />
        <div className="tw:text-body-sm tw:pt-0.5">{'Menu'}</div>
      </div>
    )
  }

  private renderNavigateButton(route: string): JSX.Element {
    const { title, icon, onClick } = this.buttonByRoute(route)
    const isSelected = this.isRouteSelected(route)
    return (
      <div
        className={classList({
          [NAV_BUTTON_BASE]: true,
          [NAV_BUTTON_SELECTED]: isSelected,
          [NAV_BUTTON_DEFAULT]: !isSelected,
        })}
        onClick={onClick}
      >
        {icon}
        <div className="tw:text-body-sm tw:pt-0.5">{title}</div>
      </div>
    )
  }

  private renderNavigateMenuButton(): JSX.Element {
    const { title, icon } = this.buttonByRoute(this.routeActiveApp)
    const isSelected = this.routeActiveApp != null || this.isMenuPopupShown

    return (
      <div
        className={classList({
          [NAV_BUTTON_BASE]: true,
          [NAV_BUTTON_SELECTED]: isSelected,
          [NAV_BUTTON_DEFAULT]: !isSelected,
          'tw:relative': true,
          'tw:pointer-events-none': this.isMenuPopupShown,
        })}
        onClick={this.toggleMenuButton}
        data-testid="bottom-nav-menu-toggle"
      >
        {icon}
        <div className="tw:text-body-sm tw:pt-0.5">{title}</div>
        <span className="tw:absolute tw:top-1.5 tw:right-1.5 tw:grow-0 tw:[&_svg]:size-[15px]">
          {this.isMenuPopupShown ? <CaretDownIcon /> : <CaretUpIcon />}
        </span>
      </div>
    )
  }

  private renderMenu() {
    const { availableAppRoutes } = this.commonStore

    return createPortal(
      <MenuCloser className={MENU_POPUP} closeMenu={this.toggleMenuButton}>
        {availableAppRoutes.map((route, i) => {
          const { title, icon, onClick } = this.buttonByRoute(route)
          const isFirst = i === 0
          const isLast = i === availableAppRoutes.length - 1
          const isActive = route === this.routeActiveApp
          return (
            <div
              key={i}
              className={classList({
                [MENU_ROW_BASE]: true,
                'tw:border-0 tw:border-t tw:border-solid tw:border-md3-outline-variant':
                  !isFirst,
                'tw:rounded-t-[10px]': isFirst,
                'tw:rounded-b-[10px]': isLast,
                'tw:bg-md3-primary-container tw:text-md3-primary tw:[&_svg]:fill-current':
                  isActive,
                'tw:text-md3-on-surface tw:[&_svg]:fill-md3-on-surface-variant tw:[&_.lucide]:text-md3-on-surface-variant':
                  !isActive,
              })}
              onClick={onClick}
              data-testid={`bottom-nav-menu-${this.getRouteTestId(route)}`}
            >
              {icon}
              <div className="tw:text-body-lg tw:mx-[15px]">{title}</div>
              {isActive && <CheckIcon className="tw:ml-auto" />}
            </div>
          )
        })}
      </MenuCloser>,
      document.body,
    )
  }

  @action.bound
  private toggleMenuButton() {
    this.isMenuPopupShown = !this.isMenuPopupShown
  }

  private openHome = () => {
    this.commonStore.displayHomeView()
  }

  private openForms = () => {
    this.commonStore.displayForms()
  }

  private openDeliveries = () => {
    this.commonStore.displayDeliveriesView()
  }

  private openChat = () => {
    this.commonStore.displayChatView()
  }

  private openPhotos = () => {
    this.commonStore.displayPhotos()
  }

  private openNotifications = () => {
    this.commonStore.displayNotifications()
  }

  private openQRCOdes = () => {
    this.commonStore.displayQRCodes()
  }

  private openEquipment = () => {
    this.commonStore.displayEquipmentView()
  }

  private openStruxTags = () => {
    this.commonStore.displayStruxTagsView()
  }

  private openMaterialResources = () => {
    this.commonStore.displayMaterialResourcesView()
  }

  private openLocations = () => {
    this.commonStore.displayLocationsView()
  }

  private isRouteSelected(route: string): boolean {
    switch (route) {
      case mobileRoutes.DELIVERIES:
        return this.commonStore.isDeliveryListDisplayed
      case mobileRoutes.FORMS:
        return this.commonStore.isFormsDisplayed
      case mobileRoutes.HOME:
        return this.commonStore.isLogisticsDisplayed
      case mobileRoutes.CHAT:
        return this.commonStore.isChatDisplayed
      case mobileRoutes.DOCUMENTS:
        return this.commonStore.isPhotosDisplayed
      case mobileRoutes.NOTIFICATIONS:
        return this.commonStore.isNotificationsDisplayed
      case mobileRoutes.QR_CODES:
        return this.commonStore.isQRCodesDisplayed
      case mobileRoutes.EQUIPMENT:
        return this.commonStore.isEquipmentDisplayed
      case mobileRoutes.STRUXTAGS:
        return this.commonStore.isStruxTagsDisplayed
      case mobileRoutes.MATERIAL_RESOURCES:
        return this.commonStore.isMaterialResourcesDisplayed
      case mobileRoutes.LOCATIONS:
        return this.commonStore.isLocationsDisplayed
      default:
        return false
    }
  }

  private get commonStore(): MobileCommonStore {
    return this.props.common
  }

  private get routeActiveApp(): string {
    if (this.commonStore.isDeliveryListDisplayed) {
      return mobileRoutes.DELIVERIES
    }
    if (this.commonStore.isFormsDisplayed) {
      return mobileRoutes.FORMS
    }
    if (this.commonStore.isPhotosDisplayed) {
      return mobileRoutes.DOCUMENTS
    }
    if (this.commonStore.isNotificationsDisplayed) {
      return mobileRoutes.NOTIFICATIONS
    }
    if (this.commonStore.isQRCodesDisplayed) {
      return mobileRoutes.QR_CODES
    }
    if (this.commonStore.isEquipmentDisplayed) {
      return mobileRoutes.EQUIPMENT
    }
    if (this.commonStore.isStruxTagsDisplayed) {
      return mobileRoutes.STRUXTAGS
    }
    if (this.commonStore.isMaterialResourcesDisplayed) {
      return mobileRoutes.MATERIAL_RESOURCES
    }
    if (this.commonStore.isLocationsDisplayed) {
      return mobileRoutes.LOCATIONS
    }
  }

  private getRouteTestId(route: string): string {
    switch (route) {
      case mobileRoutes.FORMS:
        return 'forms'
      case mobileRoutes.DELIVERIES:
        return 'deliveries'
      case mobileRoutes.NOTIFICATIONS:
        return 'notifications'
      case mobileRoutes.DOCUMENTS:
        return 'documents'
      case mobileRoutes.QR_CODES:
        return 'qr-codes'
      default:
        return route.replace(/\//g, '')
    }
  }

  private buttonByRoute = (route: string): IButton => {
    switch (route) {
      case mobileRoutes.HOME:
        return {
          title: Localization.translator.home,
          icon: <HouseIcon className={LUCIDE_IN_FILLED_SLOT} />,
          onClick: this.openHome,
        }
      case mobileRoutes.FORMS:
        return {
          title: Localization.translator.workflows,
          icon: <GenericWorkflowIcon className={LUCIDE_IN_FILLED_SLOT} />,
          onClick: this.openForms,
        }
      case mobileRoutes.DELIVERIES:
        return {
          title: Localization.translator.deliveries,
          icon: <TruckIcon className={LUCIDE_IN_FILLED_SLOT} />,
          onClick: this.openDeliveries,
        }
      case mobileRoutes.CHAT:
        return {
          title: Localization.translator.chat,
          icon: (
            <ChatIconWithBadge
              count={this.props.chatService.allUnreadMessagesCount}
            />
          ),
          onClick: this.openChat,
        }
      case mobileRoutes.DOCUMENTS:
        return {
          title: Localization.translator.photos,
          icon: <PhotoIcon />,
          onClick: this.openPhotos,
        }
      case mobileRoutes.NOTIFICATIONS: {
        const count = this.props.notificationsStore?.unreadNotificationsCount
        const badgeText = count > 99 ? '99+' : count
        return {
          title: Localization.translator.notifications,
          icon: (
            <div className="tw:relative">
              <BellIcon className={LUCIDE_IN_FILLED_SLOT} />
              {!!count && (
                <span className="tw:absolute tw:-top-1 tw:-right-1.5 tw:min-w-4 tw:h-4 tw:px-0.5 tw:flex tw:items-center tw:justify-center tw:text-[10px] tw:font-bold tw:text-destructive-foreground tw:bg-destructive tw:rounded-full tw:border tw:border-background">
                  {badgeText}
                </span>
              )}
            </div>
          ),
          onClick: this.openNotifications,
        }
      }
      case mobileRoutes.QR_CODES:
        return {
          title: Localization.translator.scanner,
          icon: <QrCodeIcon />,
          onClick: this.openQRCOdes,
        }
      case mobileRoutes.EQUIPMENT:
        return {
          title: Localization.translator.equipment,
          icon: <WrenchIcon className={LUCIDE_IN_FILLED_SLOT} />,
          onClick: this.openEquipment,
        }
      case mobileRoutes.STRUXTAGS:
        return {
          title: Localization.translator.struxTags,
          icon: <RadioIcon className={LUCIDE_IN_FILLED_SLOT} />,
          onClick: this.openStruxTags,
        }
      case mobileRoutes.MATERIAL_RESOURCES:
        return {
          title: Localization.translator.materials,
          icon: <PackageIcon className={LUCIDE_IN_FILLED_SLOT} />,
          onClick: this.openMaterialResources,
        }
      case mobileRoutes.LOCATIONS:
        return {
          title: Localization.translator.locations,
          icon: <MapPinIcon className={LUCIDE_IN_FILLED_SLOT} />,
          onClick: this.openLocations,
        }
      default:
        return {
          title: Localization.translator.apps,
          icon: <BrandStruxhubGrayIcon />,
          onClick: this.toggleMenuButton,
        }
    }
  }
}
